import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  Add,
  Add as AddIcon,
  Build as BuildIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search,
  Search as SearchIcon,
} from "@mui/icons-material";

import api from "../../../services/api";

const initialForm = {
  code: "",
  name: "",
  description: "",
  schedule_basis: "AS_REQUIRED",
  schedule_time_value: "",
  schedule_time_unit: "MONTHS",
  schedule_distance_value: "",
  schedule_distance_unit: "LAKH_KM",
  status: "ACTIVE",
};

const PAGE_SIZE = 8;

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isInteger(n) ? String(n) : String(n);
};

const scheduleText = (row) => {
  const time =
    row.schedule_time_value !== null &&
    row.schedule_time_value !== undefined &&
    row.schedule_time_value !== ""
      ? `${formatNumber(row.schedule_time_value)} ${String(
          row.schedule_time_unit || ""
        ).replace("_", " ")}`
      : "";

  const distance =
    row.schedule_distance_value !== null &&
    row.schedule_distance_value !== undefined &&
    row.schedule_distance_value !== ""
      ? `${formatNumber(row.schedule_distance_value)} ${String(
          row.schedule_distance_unit || ""
        ).replace("_", " ")}`
      : "";

  if (time && distance) return `${time} / ${distance}`;
  if (time) return time;
  if (distance) return distance;

  if (row.schedule_basis === "AS_PRESCRIBED") return "As prescribed";
  return "As required";
};

const RepairTypeMaster = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);

  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    type: "",
    text: "",
  });

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await api.get("/repair-type");

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];

      setRows(data);
    } catch (error) {
      console.error(error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Unable to load repair type master.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((item) =>
      [
        item.code,
        item.name,
        item.description,
        scheduleText(item),
        item.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, search]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));

  const visibleRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(1);
  };

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm(initialForm);
    setMessage({ type: "", text: "" });
    setOpen(true);
  };

  const handleOpenEdit = (row) => {
    setEditingId(row.id);

    setForm({
      code: row.code || "",
      name: row.name || "",
      description: row.description || "",
      schedule_basis: row.schedule_basis || "AS_REQUIRED",
      schedule_time_value:
        row.schedule_time_value === null ||
        row.schedule_time_value === undefined
          ? ""
          : row.schedule_time_value,
      schedule_time_unit: row.schedule_time_unit || "MONTHS",
      schedule_distance_value:
        row.schedule_distance_value === null ||
        row.schedule_distance_value === undefined
          ? ""
          : row.schedule_distance_value,
      schedule_distance_unit: row.schedule_distance_unit || "LAKH_KM",
      status: row.status || "ACTIVE",
    });

    setMessage({ type: "", text: "" });
    setOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBasisChange = (event) => {
    const value = event.target.value;

    setForm((prev) => ({
      ...prev,
      schedule_basis: value,
      ...(value === "AS_REQUIRED" || value === "AS_PRESCRIBED"
        ? {
            schedule_time_value: "",
            schedule_time_unit: "MONTHS",
            schedule_distance_value: "",
            schedule_distance_unit: "LAKH_KM",
          }
        : {}),
      ...(value === "TIME"
        ? {
            schedule_distance_value: "",
            schedule_distance_unit: "LAKH_KM",
          }
        : {}),
      ...(value === "DISTANCE"
        ? {
            schedule_time_value: "",
            schedule_time_unit: "MONTHS",
          }
        : {}),
    }));
  };

  const validate = () => {
    if (!form.code.trim() || !form.name.trim()) {
      setMessage({
        type: "error",
        text: "Code and name are required.",
      });
      return false;
    }

    if (
      form.schedule_basis === "TIME" &&
      form.schedule_time_value === ""
    ) {
      setMessage({
        type: "error",
        text: "Schedule time is required.",
      });
      return false;
    }

    if (
      form.schedule_basis === "DISTANCE" &&
      form.schedule_distance_value === ""
    ) {
      setMessage({
        type: "error",
        text: "Schedule distance is required.",
      });
      return false;
    }

    if (
      form.schedule_basis === "TIME_OR_DISTANCE" &&
      form.schedule_time_value === "" &&
      form.schedule_distance_value === ""
    ) {
      setMessage({
        type: "error",
        text: "Enter at least one time or distance schedule.",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      setSaving(true);
      setMessage({ type: "", text: "" });

      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        schedule_time_value:
          form.schedule_time_value === ""
            ? null
            : Number(form.schedule_time_value),
        schedule_distance_value:
          form.schedule_distance_value === ""
            ? null
            : Number(form.schedule_distance_value),
      };

      if (editingId) {
        await api.put(`/repair-type/${editingId}`, payload);
      } else {
        await api.post("/repair-type", payload);
      }

      setOpen(false);
      setMessage({
        type: "success",
        text: editingId
          ? "Repair type updated successfully."
          : "Repair type added successfully.",
      });

      await loadRows();
    } catch (error) {
      console.error(error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Unable to save repair type.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setLoading(true);

      await api.delete(`/repair-type/${deleteId}`);

      setDeleteId(null);

      setMessage({
        type: "success",
        text: "Repair type deleted successfully.",
      });

      await loadRows();
    } catch (error) {
      console.error(error);
      setMessage({
        type: "error",
        text:
          error.response?.data?.message ||
          "Unable to delete repair type.",
      });
    } finally {
      setLoading(false);
    }
  };

  const statusColor = (status) =>
    status === "ACTIVE" ? "success" : "default";

  const renderScheduleFields = () => {
    const showTime =
      form.schedule_basis === "TIME" ||
      form.schedule_basis === "TIME_OR_DISTANCE";

    const showDistance =
      form.schedule_basis === "DISTANCE" ||
      form.schedule_basis === "TIME_OR_DISTANCE";

    if (!showTime && !showDistance) {
      return (
        <Alert severity="info" sx={{ mt: 1 }}>
          {form.schedule_basis === "AS_PRESCRIBED"
            ? "Schedule is maintained as prescribed by the applicable maintenance schedule."
            : "Repair is carried out as required."}
        </Alert>
      );
    }

    return (
      <Grid container spacing={2}>
        {showTime && (
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                label="Time Period"
                name="schedule_time_value"
                type="number"
                value={form.schedule_time_value}
                onChange={handleChange}
                inputProps={{ min: 0, step: "0.01" }}
              />

              <TextField
                select
                sx={{ minWidth: 130 }}
                label="Unit"
                name="schedule_time_unit"
                value={form.schedule_time_unit}
                onChange={handleChange}
              >
                <MenuItem value="MONTHS">Months</MenuItem>
                <MenuItem value="YEARS">Years</MenuItem>
              </TextField>
            </Stack>
          </Grid>
        )}

        {showDistance && (
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                label="Distance Period"
                name="schedule_distance_value"
                type="number"
                value={form.schedule_distance_value}
                onChange={handleChange}
                inputProps={{ min: 0, step: "0.01" }}
              />

              <TextField
                select
                sx={{ minWidth: 140 }}
                label="Unit"
                name="schedule_distance_unit"
                value={form.schedule_distance_unit}
                onChange={handleChange}
              >
                <MenuItem value="KM">KM</MenuItem>
                <MenuItem value="LAKH_KM">Lakh KM</MenuItem>
              </TextField>
            </Stack>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", p: { xs: 1, sm: 2 } }}>
      <Card
        elevation={0}
        sx={{
          width: "100%",
          border: "1px solid #e1e7ef",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" spacing={1.2} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                }}
              >
                <BuildIcon />
              </Box>

              <Box>
                <Typography variant="h6" fontWeight={700}>
                  Repair Type Master
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage repair types and scheduled maintenance periods
                </Typography>
              </Box>
            </Stack>

            {/* <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenAdd}
              fullWidth={isMobile}
            >
              Add Repair Type
            </Button> */}
          </Stack>

          <Divider sx={{ my: 2 }} />

          {message.text && (
            <Alert
              severity={message.type || "info"}
              onClose={() => setMessage({ type: "", text: "" })}
              sx={{ mb: 2 }}
            >
              {message.text}
            </Alert>
          )}

          {/* <TextField
            fullWidth
            size="small"
            value={search}
            onChange={handleSearch}
            placeholder="Search by code, name, description or schedule..."
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{ mr: 1, color: "text.secondary" }}
                />
              ),
            }}
          /> */}

            <Box
          sx={{
            p: 2,

            display: "flex",

            gap: 2,

            justifyContent: "space-between",

            alignItems: {
              xs: "stretch",
              sm: "center",
            },

            flexDirection: {
              xs: "column",
              sm: "row",
            },
          }}
        >
          <TextField
            size="small"
            placeholder="Search by code, name, description or schedule..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: {
                xs: "100%",
                sm: 380,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{
              xs: "space-between",
              sm: "flex-end",
            }}
          >
            <Chip label={`${filteredRows.length} Records`} variant="outlined" />

            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAdd}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 2.5,
                whiteSpace: "nowrap",
              }}
            >
              Add Repair Type
            </Button>
          </Stack>
        </Box>

          <Box sx={{ mt: 2 }}>
            {!isMobile ? (
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ width: "100%", overflowX: "auto" }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width={60}>#</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Repair Type</TableCell>
                      <TableCell>Schedule Period</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {visibleRows.map((row, index) => (
                      <TableRow hover key={row.id}>
                        <TableCell>
                          {(page - 1) * PAGE_SIZE + index + 1}
                        </TableCell>

                        <TableCell>
                          <Typography fontWeight={700}>
                            {row.code}
                          </Typography>
                        </TableCell>

                        <TableCell>{row.name}</TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={scheduleText(row)}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell
                          sx={{
                            maxWidth: 300,
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {row.description || "-"}
                        </TableCell>

                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status}
                            color={statusColor(row.status)}
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="Edit">
                            <IconButton
                              color="primary"
                              onClick={() => handleOpenEdit(row)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => setDeleteId(row.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}

                    {!visibleRows.length && (
                      <TableRow>
                        <TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            {loading
                              ? "Loading..."
                              : "No repair types found."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Stack spacing={1.5}>
                {visibleRows.map((row, index) => (
                  <Card
                    key={row.id}
                    variant="outlined"
                    sx={{ width: "100%", borderRadius: 2 }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Stack spacing={1.2}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={800}>
                              {(page - 1) * PAGE_SIZE + index + 1}.{" "}
                              {row.code}
                            </Typography>
                            <Typography
                              fontWeight={600}
                              sx={{
                                overflowWrap: "anywhere",
                              }}
                            >
                              {row.name}
                            </Typography>
                          </Box>

                          <Chip
                            size="small"
                            label={row.status}
                            color={statusColor(row.status)}
                          />
                        </Stack>

                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            Schedule Period
                          </Typography>
                          <Typography fontWeight={700}>
                            {scheduleText(row)}
                          </Typography>
                        </Box>

                        {row.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              whiteSpace: "normal",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {row.description}
                          </Typography>
                        )}

                        <Stack
                          direction="row"
                          justifyContent="flex-end"
                          spacing={0.5}
                        >
                          <IconButton
                            color="primary"
                            onClick={() => handleOpenEdit(row)}
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() => setDeleteId(row.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

                {!visibleRows.length && (
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    {loading ? "Loading..." : "No repair types found."}
                  </Typography>
                )}
              </Stack>
            )}
          </Box>

          {filteredRows.length > PAGE_SIZE && (
            <Stack alignItems="center" sx={{ mt: 2 }}>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* ADD / EDIT */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingId ? "Edit Repair Type" : "Add Repair Type"}
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Code"
                name="code"
                value={form.code}
                onChange={handleChange}
                inputProps={{ maxLength: 30 }}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                label="Repair Type Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                inputProps={{ maxLength: 150 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                name="description"
                value={form.description}
                onChange={handleChange}
                inputProps={{ maxLength: 500 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Schedule Basis"
                name="schedule_basis"
                value={form.schedule_basis}
                onChange={handleBasisChange}
              >
                <MenuItem value="TIME">Time Period</MenuItem>
                <MenuItem value="DISTANCE">Distance</MenuItem>
                <MenuItem value="TIME_OR_DISTANCE">
                  Time or Distance — whichever is earlier
                </MenuItem>
                <MenuItem value="AS_PRESCRIBED">
                  As Prescribed
                </MenuItem>
                <MenuItem value="AS_REQUIRED">As Required</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              {renderScheduleFields()}
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography fontWeight={600}>Status</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Enable or disable this repair type.
                  </Typography>
                </Box>

                <Switch
                  checked={form.status === "ACTIVE"}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      status: event.target.checked
                        ? "ACTIVE"
                        : "INACTIVE",
                    }))
                  }
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={saving}>
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Saving..." : editingId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE */}
      <Dialog
        open={Boolean(deleteId)}
        onClose={() => !loading && setDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Repair Type</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete this repair type?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeleteId(null)}
            disabled={loading}
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RepairTypeMaster;
